import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'react-hot-toast'

import App from './App'
import './styles/index.css'
import './utils/fontawesome' // 导入Font Awesome配置

console.log('main.tsx: Application starting');

// 添加错误边界来捕获渲染错误
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('ErrorBoundary: Caught error', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary: Error caught', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">出错了</h1>
            <p className="text-gray-800 mb-4">应用渲染时发生错误：</p>
            <pre className="bg-gray-100 p-3 rounded overflow-auto text-sm mb-4">
              {this.state.error?.message || '未知错误'}
            </pre>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              onClick={() => window.location.reload()}
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 创建 React Query 客户端
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5分钟
    },
  },
})

console.log('main.tsx: Creating React root');

// 确保 root 元素存在
const rootElement = document.getElementById('root');
console.log('main.tsx: Root element', rootElement);

if (!rootElement) {
  console.error('main.tsx: Root element not found');
  document.body.innerHTML = '<div style="color: red; padding: 20px;">错误：找不到根元素 #root</div>';
} else {
  console.log('main.tsx: Rendering React app');
  
  try {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ErrorBoundary>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <QueryClientProvider client={queryClient}>
              <App />
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    style: {
                      background: '#10b981',
                    },
                  },
                  error: {
                    style: {
                      background: '#ef4444',
                    },
                  },
                }}
              />
              {/* 开发环境显示 React Query DevTools */}
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>,
    )
    console.log('main.tsx: React app rendered successfully');
  } catch (error) {
    console.error('main.tsx: Error rendering React app', error);
    rootElement.innerHTML = `
      <div style="color: red; padding: 20px;">
        <h2>渲染错误</h2>
        <p>${error instanceof Error ? error.message : '未知错误'}</p>
      </div>
    `;
  }
}